import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sparkles, SendHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import {
  consumeComposerChatStream,
  getLiveComposerDocumentContent,
  type ComposerChatDonePayload,
} from '@/lib/composerChatStream';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  citations?: { label: string }[];
  hasInsertableContent?: boolean;
  chatMode: string;
  researchNoResults?: boolean;
  isStreaming?: boolean;
  isSearching?: boolean;
}

interface VeraChatProps {
  sessionId: string | null;
  clinicId: string;
  documentContent: string;
}

export function VeraChat({ sessionId, clinicId, documentContent }: VeraChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const [chatMode, setChatMode] = useState<'my_sources' | 'research'>('my_sources');
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!sessionId) return;
    supabase
      .from('composer_chat_history')
      .select('id, role, content, citations, chat_mode')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true })
      .then(({ data }) => {
        if (data) {
          setMessages(
            data.map((row) => ({
              id: row.id,
              role: row.role as 'user' | 'assistant',
              content: row.content,
              citations: row.citations as { label: string }[] | undefined,
              hasInsertableContent: row.content.includes('[[INSERT_START]]'),
              chatMode: row.chat_mode,
            }))
          );
        }
      });
  }, [sessionId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const handleTextareaChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    const ta = e.target;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
  }, []);

  const sendStreamMessage = useCallback(
    async (trimmed: string, assistantId: string, mode: 'my_sources' | 'research') => {
      const liveDocumentContent = getLiveComposerDocumentContent(documentContent);

      const { data, error } = await supabase.functions.invoke('composer-chat', {
        body: {
          session_id: sessionId,
          message: trimmed,
          document_content: liveDocumentContent,
          chat_mode: mode,
        },
      });

      if (error) throw error;
      if (!(data instanceof Response)) {
        throw new Error('Expected streaming response from composer-chat');
      }

      if (!data.ok) {
        const errBody = await data.json().catch(() => ({})) as { error?: string; response?: string };
        throw new Error(errBody.response ?? errBody.error ?? 'Request failed');
      }

      const contentType = data.headers.get('Content-Type') ?? '';
      if (!contentType.includes('text/event-stream')) {
        throw new Error('Unexpected response type from composer-chat');
      }

      let receivedDelta = false;
      let doneReceived = false;

      await consumeComposerChatStream(data, {
        onStatus: (phase) => {
          if (phase === 'searching') {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantId ? { ...msg, isSearching: true } : msg
              )
            );
          }
        },
        onDelta: (text) => {
          if (!receivedDelta) {
            receivedDelta = true;
            setIsLoading(false);
            setStreamingMessageId(assistantId);
          }
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantId
                ? { ...msg, content: msg.content + text, isSearching: false }
                : msg
            )
          );
        },
        onDone: (payload: ComposerChatDonePayload) => {
          doneReceived = true;
          const responseText = payload.response_text ?? '';
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantId
                ? {
                    ...msg,
                    content: responseText,
                    citations: payload.citations?.length ? payload.citations : undefined,
                    hasInsertableContent:
                      payload.has_insertable_content ?? responseText.includes('[[INSERT_START]]'),
                    chatMode: payload.chat_mode ?? mode,
                    researchNoResults: payload.research_no_results,
                    isStreaming: false,
                    isSearching: false,
                  }
                : msg
            )
          );
          setStreamingMessageId(null);
        },
        onError: (message) => {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantId
                ? { ...msg, content: message, isStreaming: false, isSearching: false }
                : msg
            )
          );
          setStreamingMessageId(null);
        },
      });

      if (!doneReceived) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantId
              ? {
                  ...msg,
                  hasInsertableContent: msg.content.includes('[[INSERT_START]]'),
                  isStreaming: false,
                  isSearching: false,
                }
              : msg
          )
        );
        setStreamingMessageId(null);
      }
    },
    [sessionId, documentContent]
  );

  const sendMessage = useCallback(async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isLoading || streamingMessageId) return;

    if (!sessionId) {
      toast.info('Start typing in the editor to begin your session.');
      return;
    }

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: trimmed,
      chatMode,
    };

    const assistantId = crypto.randomUUID();

    setMessages((prev) => [
      ...prev,
      userMsg,
      {
        id: assistantId,
        role: 'assistant',
        content: '',
        chatMode,
        isStreaming: true,
        isSearching: chatMode === 'research',
      },
    ]);
    setInputValue('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    setIsLoading(true);

    try {
      await sendStreamMessage(trimmed, assistantId, chatMode);
    } catch (err) {
      console.error('[VeraChat] sendMessage failed:', err);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantId
            ? { ...msg, content: 'Something went wrong. Please try again.', isStreaming: false, isSearching: false }
            : msg
        )
      );
    } finally {
      setIsLoading(false);
      setStreamingMessageId(null);
    }
  }, [inputValue, isLoading, streamingMessageId, sessionId, chatMode, sendStreamMessage]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    },
    [sendMessage]
  );

  const handleInsert = useCallback((content: string) => {
    const regex = /\[\[INSERT_START\]\]([\s\S]*?)\[\[INSERT_END\]\]/g;
    const blocks: string[] = [];
    let match;
    while ((match = regex.exec(content)) !== null) {
      blocks.push(match[1].trim());
    }
    if (blocks.length === 0) return;
    const html = blocks.join('\n');
    (window as any).__composerInsertContent?.(html);
    toast.success('Inserted into editor');
  }, []);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-border flex-shrink-0">
        <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground py-1 mt-px">
          Vera
        </span>
        <div className="flex rounded-full bg-muted p-0.5 mt-px">
          <button
            onClick={() => setChatMode('my_sources')}
            className={`text-xs px-3 py-1 rounded-full transition-colors ${
              chatMode === 'my_sources'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            My Sources
          </button>
          <button
            onClick={() => setChatMode('research')}
            className={`text-xs px-3 py-1 rounded-full transition-colors ${
              chatMode === 'research'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Research
          </button>
        </div>
      </div>

      <ScrollArea className="flex-1 min-h-0">
        <div className="flex flex-col gap-3 p-4">
          {messages.length === 0 && !isLoading ? (
            <div className="flex flex-col items-center justify-center h-full min-h-[300px] gap-2 text-center">
              <Sparkles className="h-8 w-8 text-muted-foreground/50" />
              <p className="text-sm font-medium text-muted-foreground">
                Ask Vera anything about your policy.
              </p>
              <p className="text-xs text-muted-foreground/70">
                {chatMode === 'research'
                  ? 'Research mode: Vera can search the web.'
                  : 'Vera only references your uploaded Sources.'}
              </p>
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className="flex flex-col max-w-[85%] gap-1.5">
                  {msg.role === 'assistant' && msg.chatMode === 'research' && (
                    <span className="text-xs text-muted-foreground uppercase tracking-wide px-1">
                      Research
                    </span>
                  )}
                  {msg.role === 'assistant' && msg.isSearching && (
                    <span className="text-xs text-muted-foreground uppercase tracking-wide px-1">
                      Searching…
                    </span>
                  )}
                  <div
                    className={`rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap ${
                      msg.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : msg.researchNoResults
                          ? 'bg-muted/60 text-muted-foreground border border-dashed border-muted-foreground/30'
                          : msg.chatMode === 'research'
                            ? 'bg-muted text-foreground border-l-2 border-primary'
                            : 'bg-muted text-foreground'
                    }`}
                  >
                    {msg.content
                      ? msg.content
                          .replace(/\[\[INSERT_START\]\]/g, '')
                          .replace(/\[\[INSERT_END\]\]/g, '')
                      : null}
                    {msg.isStreaming && !msg.content && !msg.isSearching && (
                      <span className="inline-flex gap-1 py-0.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:0ms]" />
                        <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:150ms]" />
                        <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:300ms]" />
                      </span>
                    )}
                    {msg.isSearching && !msg.content && (
                      <span className="inline-flex gap-1 py-0.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:0ms]" />
                        <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:150ms]" />
                        <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:300ms]" />
                      </span>
                    )}
                    {msg.isStreaming && msg.content && (
                      <span className="inline-block w-0.5 h-4 bg-muted-foreground/60 animate-pulse ml-0.5 align-text-bottom" />
                    )}
                  </div>

                  {msg.role === 'assistant' && !msg.isStreaming && msg.citations && msg.citations.filter(c => !c.label.includes('INSERT_START') && !c.label.includes('INSERT_END')).length > 0 && (
                    <div className="flex flex-wrap gap-1 px-1">
                      {msg.citations.filter(c => !c.label.includes('INSERT_START') && !c.label.includes('INSERT_END')).map((c, i) => (
                        <span
                          key={i}
                          className="text-[10px] text-muted-foreground bg-muted/60 rounded px-1.5 py-0.5"
                        >
                          [{c.label}]
                        </span>
                      ))}
                    </div>
                  )}

                  {msg.role === 'assistant' && !msg.isStreaming && msg.hasInsertableContent && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full text-xs"
                      onClick={() => handleInsert(msg.content)}
                    >
                      Insert into editor
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
          <div ref={bottomRef} />
        </div>
      </ScrollArea>

      <div className="flex items-end gap-2 border-t border-border pb-10 pt-2 px-3 flex-shrink-0">
        <Textarea
          ref={textareaRef}
          value={inputValue}
          onChange={handleTextareaChange}
          onKeyDown={handleKeyDown}
          placeholder="Ask Vera..."
          className="min-h-[38px] max-h-[120px] resize-none text-sm py-2"
          rows={1}
        />
        <Button
          size="icon"
          onClick={sendMessage}
          disabled={!inputValue.trim() || isLoading || !!streamingMessageId}
          className="h-[38px] w-[38px] flex-shrink-0"
        >
          <SendHorizontal className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
